import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Popconfirm, Radio, Row, Spin, message } from 'antd';
import { ReloadOutlined, RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { callCenterCallsApi } from '../../utils/api';
import PersianDatePicker from '../PersianDatePicker';

const toLatinDigits = (value) =>
  String(value ?? '')
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

const formatPickerDate = (value) => {
  if (!value) return undefined;
  const raw = typeof value === 'string' ? value : value?.format?.('YYYY/MM/DD');
  return raw ? toLatinDigits(raw) : undefined;
};

const MODE_OPTIONS = [
  { value: 'missing', label: 'فقط تماس‌های بدون خلاصه (و ناموفق)' },
  { value: 'failed', label: 'فقط خلاصه‌سازی‌های ناموفق' },
  { value: 'all', label: 'همه (بازتولید خلاصه‌های موجود)' },
];

const STAT_TILES = [
  { key: 'transcribed', label: 'رونویسی‌شده', className: 'text-gray-800 dark:text-gray-100' },
  { key: 'completed', label: 'خلاصه‌شده', className: 'text-emerald-600' },
  { key: 'missing', label: 'بدون خلاصه', className: 'text-amber-600' },
  { key: 'failed', label: 'ناموفق', className: 'text-red-500' },
  { key: 'queued', label: 'در صف / در حال پردازش', className: 'text-purple-600' },
];

/**
 * خلاصه‌سازی گروهی تماس‌های قدیمیِ رونویسی‌شده (با آمار زنده و تخمین تعداد قبل از ارسال).
 *
 * @param {object} baseFilters فیلترهای ثابت (مثلاً فیلترهای فعلی لیست تماس‌ها)
 * @param {boolean} showDateRange نمایش انتخاب بازه تاریخ
 * @param {() => void} onQueued پس از ارسال موفق
 */
const AiBulkSummaryCard = ({ baseFilters, showDateRange = true, onQueued }) => {
  const [range, setRange] = useState({ from: null, to: null });
  const [mode, setMode] = useState('missing');
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const filters = useMemo(() => {
    const f = { ...(baseFilters || {}) };
    const from = formatPickerDate(range.from);
    const to = formatPickerDate(range.to);
    if (from) f.from = from;
    if (to) f.to = to;
    return f;
  }, [baseFilters, range]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await callCenterCallsApi.getSummaryStats(filters);
      setStats(res?.data ?? null);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت آمار خلاصه‌سازی');
    } finally {
      setLoadingStats(false);
    }
  }, [filters]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const estimate = useMemo(() => {
    if (!stats) return 0;
    if (mode === 'failed') return stats.failed;
    if (mode === 'missing') return stats.missing + stats.failed;
    return stats.transcribed - stats.pending - stats.processing;
  }, [stats, mode]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await callCenterCallsApi.summarizeBulk({ ...filters, mode });
      message.success(res?.message || 'تماس‌ها در صف خلاصه‌سازی قرار گرفتند');
      if (res?.data?.limited) {
        message.warning(`حداکثر ${res.data.limit} تماس در هر درخواست ارسال می‌شود؛ برای باقی موارد دوباره اجرا کنید.`);
      }
      await loadStats();
      onQueued?.(res?.data);
    } catch (error) {
      message.error(error?.message || 'خطا در ارسال درخواست خلاصه‌سازی گروهی');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="custom-font space-y-4">
      {stats && !stats.summary_ready && (
        <Alert
          type="warning"
          showIcon
          className="custom-font"
          message="خلاصه‌سازی هوش مصنوعی غیرفعال است یا کلید GapGPT ثبت نشده است. ابتدا تنظیمات را تکمیل و ذخیره کنید."
        />
      )}

      <Spin spinning={loadingStats}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {STAT_TILES.map((tile) => {
            const value = tile.key === 'queued'
              ? (stats?.pending ?? 0) + (stats?.processing ?? 0)
              : stats?.[tile.key] ?? 0;
            return (
              <div
                key={tile.key}
                className="p-3 rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-800/60 text-center"
              >
                <div className={`text-xl font-bold ${tile.className}`}>{value.toLocaleString('fa-IR')}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tile.label}</div>
              </div>
            );
          })}
        </div>
      </Spin>

      {showDateRange && (
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <div className="text-xs text-gray-500 mb-1">از تاریخ (اختیاری)</div>
            <PersianDatePicker
              value={range.from}
              onChange={(value) => setRange((prev) => ({ ...prev, from: value }))}
              placeholder="ابتدای بازه"
            />
          </Col>
          <Col xs={24} md={12}>
            <div className="text-xs text-gray-500 mb-1">تا تاریخ (اختیاری)</div>
            <PersianDatePicker
              value={range.to}
              onChange={(value) => setRange((prev) => ({ ...prev, to: value }))}
              placeholder="انتهای بازه"
            />
          </Col>
        </Row>
      )}

      <Radio.Group
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        className="custom-font flex flex-col gap-2"
        options={MODE_OPTIONS}
      />

      <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          تعداد تقریبی تماس‌هایی که ارسال می‌شوند: <b className="text-purple-600">{estimate.toLocaleString('fa-IR')}</b>
          {' '}— پردازش در پس‌زمینه و با رعایت سقف درخواست در دقیقه انجام می‌شود.
        </span>
        <div className="flex items-center gap-2">
          <Button icon={<ReloadOutlined />} onClick={loadStats} loading={loadingStats} className="custom-font">
            بروزرسانی آمار
          </Button>
          <Popconfirm
            title={<span className="custom-font">{`${estimate.toLocaleString('fa-IR')} تماس به هوش مصنوعی ارسال شود؟`}</span>}
            description={<span className="custom-font text-xs">هر تماس یک درخواست به GapGPT است و هزینه دارد.</span>}
            okText="ارسال"
            cancelText="انصراف"
            onConfirm={handleSubmit}
            disabled={!estimate || !stats?.summary_ready}
          >
            <Button
              type="primary"
              icon={mode === 'all' ? <ThunderboltOutlined /> : <RobotOutlined />}
              loading={submitting}
              disabled={!estimate || !stats?.summary_ready}
              className="custom-font"
            >
              شروع خلاصه‌سازی گروهی
            </Button>
          </Popconfirm>
        </div>
      </div>
    </div>
  );
};

export default AiBulkSummaryCard;
